import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadAPI } from '../api';
import { toast } from 'sonner';
import { ArrowLeft, RotateCcw, Trash2, AlertCircle } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';

export const RecycleBinPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await leadAPI.getRecycleBin(); setLeads(data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load Recycle Bin'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const restore = async (id) => {
    try { await leadAPI.restore(id); toast.success('Lead restored'); fetch(); }
    catch { toast.error('Restore failed'); }
  };
  const purge = async (id) => {
    if (!window.confirm('Permanently delete this lead? This cannot be undone.')) return;
    try { await leadAPI.permanentDelete(id); toast.success('Permanently deleted'); fetch(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="recycle-bin-container">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/leads')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex-1">
          <h1 className="text-3xl font-outfit font-semibold">Recycle Bin</h1>
          <p className="text-muted-foreground">Deleted leads auto-purge after 30 days</p>
        </div>
      </div>

      <div className="clay-card p-4 border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm">Only <strong>Super Admin</strong> can restore or permanently delete. Leads older than 30 days are automatically removed on backend startup.</p>
      </div>

      {loading ? <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div></div> :
       leads.length === 0 ? <div className="clay-card p-8 text-center text-sm text-muted-foreground">Recycle Bin is empty.</div> :
       <div className="clay-card overflow-hidden">
         <table className="w-full text-sm">
           <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
             <tr>{['Customer','Mobile','Status','Deleted','Days Left','Actions'].map((h,i)=><th key={i} className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">{h}</th>)}</tr>
           </thead>
           <tbody>
             {leads.map(l => {
               const deletedAt = l.deleted_at ? new Date(l.deleted_at) : null;
               const daysElapsed = deletedAt && !isNaN(deletedAt) ? differenceInDays(new Date(), deletedAt) : 0;
               const daysLeft = Math.max(30 - daysElapsed, 0);
               return (
                 <tr key={l.id} data-testid={`recycle-lead-${l.id}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                   <td className="px-4 py-3 font-medium">{l.customer_name}</td>
                   <td className="px-4 py-3">{l.primary_mobile}</td>
                   <td className="px-4 py-3 text-xs">{(l.status || '').replace(/_/g, ' ')}</td>
                   <td className="px-4 py-3 text-xs">{deletedAt && !isNaN(deletedAt) ? format(deletedAt, 'MMM d, yyyy') : '—'}</td>
                   <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${daysLeft <= 3 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{daysLeft} days</span></td>
                   <td className="px-4 py-3">
                     <div className="flex gap-1">
                       <button onClick={() => restore(l.id)} data-testid={`rb-restore-${l.id}`} className="p-1.5 bg-green-50 text-green-700 rounded hover:bg-green-100" title="Restore"><RotateCcw className="h-3 w-3" /></button>
                       <button onClick={() => purge(l.id)} data-testid={`rb-permanent-${l.id}`} className="p-1.5 bg-red-50 text-red-700 rounded hover:bg-red-100" title="Permanent delete"><Trash2 className="h-3 w-3" /></button>
                     </div>
                   </td>
                 </tr>
               );
             })}
           </tbody>
         </table>
       </div>
      }
    </div>
  );
};
export default RecycleBinPage;
